/* eslint-disable prettier/prettier */
import PropTypes from "prop-types";
import {memo, useCallback, useState} from "react";
import get from "lodash/get";
import LazyLoad from "react-lazyload";
import Video from "app/pages/.shared/Video/Video";
import "./AccommodationSection.scss";
import SanitaryMessage from "app/pages/FicheProduit/components/Accommodation/SanitaryMessage/SanitaryMessage";
import {FormattedMessage} from "react-intl";
import ProductNameWithCategory from "app/pages/.shared/ProductNameWithCategory/ProductNameWithCategory";
import Paragraphs from "app/pages/.shared/Paragraphs";
import MoreDetails from "app/pages/.shared/MoreDetails";
import Map from "app/pages/.shared/Map/Map";
import ProductRatingContainer from "app/pages/FicheProduit/components/ProductRating/ProductRatingContainer";
import InteractiveMap from "app/pages/.shared/Map/InteractiveMap/InteractiveMap";
import Typography, {TYPOGRAPHY_VARIANTS} from "app/pages/.shared/Typography/Typography";

const AccommodationSection = ({
                                  headline,
                                  name,
                                  videoUrl,
                                  displayVideo = true,
                                  videoType,
                                  category,
                                  accommodation = {},
                                  tripAdvisor = {},
                                  holidayCheck = {},
                                  gpsLat,
                                  gpsLong,
                                  sanitaryInformations = {},
                                  interactiveMap = {},
                                  googleAPIKey,
                              }) => {
    const [showAwardLabel, setShowAwardLabel] = useState(Boolean(tripAdvisor?.awardLabelUrl));

    const handleAwardLabelLoadError = useCallback(() => {
        setShowAwardLabel(false);
    }, []);

    const accommodationName = <ProductNameWithCategory name={name} category={category}/>;

    const showMap = gpsLat !== 0 && gpsLong !== 0 && googleAPIKey;

    const mapComponent =
        interactiveMap.url && interactiveMap.thumbnail ? (
            <div className="accommodation-bloc__map">
                <InteractiveMap url={interactiveMap.url} thumbnail={interactiveMap.thumbnail}/>
            </div>
        ) : (
            showMap && (
                <div className="accommodation-bloc__map">
                    <Map latitude={gpsLat} longitude={gpsLong} zoom={10}/>
                </div>
            )
        );

    return (
        <div id="accommodation" className="accommodation-bloc">
            {headline && (
                <Typography
                    variant={TYPOGRAPHY_VARIANTS.XL}
                    variantMD={TYPOGRAPHY_VARIANTS.XL4}
                    isBold
                    className="accommodation-bloc__title"
                    component="h2"
                >
                    {headline}
                </Typography>
            )}

            <div className="accommodation-bloc__header">
                <div className="accommodation-bloc__hotel-name">
                    {accommodationName}

                    <ProductRatingContainer
                        locationName={accommodationName}
                        tripAdvisor={{
                            ...tripAdvisor,
                            awardLabel: (
                                <>
                                    {showAwardLabel && tripAdvisor.awardLabelUrl && (
                                        <img
                                            className="accommodation-bloc__certification"
                                            src={tripAdvisor.awardLabelUrl}
                                            onError={handleAwardLabelLoadError}
                                        />
                                    )}
                                </>
                            ),
                        }}
                        holidayCheck={{
                            ...holidayCheck,
                            size: "small",
                        }}
                    />
                </div>
            </div>

            {displayVideo && videoUrl && videoUrl.indexOf("http") !== -1 && (
                <div className={!videoType && "accommodation-bloc__video"}>
                    <LazyLoad once offset={300}>
                        <Video url={videoUrl} videoType={videoType}/>
                    </LazyLoad>
                </div>
            )}

            <div className="accommodation-bloc__intro">
                <Paragraphs
                    paragraphs={get(accommodation, "intro.intro")}
                    enableHTMLFormatting={true}
                />
            </div>

            {get(accommodation, "facilities.length") > 0 && (
                <div className="accommodation-bloc__details">
                    <MoreDetails
                        expandedLabel={<FormattedMessage id="product.page.less.details"/>}
                        collapsedLabel={<FormattedMessage id="product.page.more.details"/>}
                    >
                        <Paragraphs paragraphs={accommodation.facilities} enableHTMLFormatting/>
                    </MoreDetails>
                </div>
            )}

            {mapComponent}

            <SanitaryMessage
                intro={sanitaryInformations.intro}
                details={sanitaryInformations.moreDetails}
            />
        </div>
    );
};

AccommodationSection.propTypes = {
    headline: PropTypes.string,
    gpsLat: PropTypes.number,
    gpsLong: PropTypes.number,
    videoUrl: PropTypes.string,
    videoType: PropTypes.string,
    name: PropTypes.string,
    category: PropTypes.number,
    displayVideo: PropTypes.bool,
    accommodation: PropTypes.object,
    sanitaryInformations: PropTypes.object,
    tripAdvisor: PropTypes.object,
    holidayCheck: PropTypes.object,
    interactiveMap: PropTypes.object,
    googleAPIKey: PropTypes.string,
};

export default memo(AccommodationSection);
